import api from './api';

export interface GlobalStats {
  interventions: {
    total: number;
    completed: number;
    pending: number;
    inProgress: number;
    byStatus: Array<{ status: string; count: number }>;
    byMonth: Array<{ month: string; count: number }>;
  };
  orders: {
    total: number;
    revenue: number;
    grossRevenue?: number;
    totalRefunds?: number;
    refundsCount?: number;
    netRevenue?: number;
    totalPayments?: number;
    pending: number;
    revenueByMonth: Array<{ month: string; revenue: number }>;
  };
  revenue?: {
    gross: number;
    refunds: number;
    refundsCount: number;
    net: number;
  };
  quotes: {
    total: number;
    accepted: number;
    rejected: number;
    pending: number;
    conversionRate: string;
  };
  complaints: {
    total: number;
    open: number;
    resolved: number;
  };
  users: {
    total: number;
    technicians: number;
    customers: number;
  };
}

export interface TechnicianPerformance {
  technicianId: number;
  name: string;
  email: string;
  totalInterventions: number;
  completedInterventions: number;
  completionRate: number;
  avgRating: string;
  specialty: string;
}

export interface ChartData {
  success: boolean;
  chartType: string;
  data: any[];
}

export interface ChartDataParams {
  period?: number;
  startDate?: string;
  endDate?: string;
}

class AnalyticsService {
  /**
   * Récupérer les statistiques globales
   */
  async getGlobalStats(startDate?: string, endDate?: string): Promise<GlobalStats> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/analytics/stats?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Récupérer les performances des techniciens
   */
  async getTechnicianPerformance(startDate?: string, endDate?: string): Promise<TechnicianPerformance[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/analytics/technicians?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Récupérer les données pour un graphique spécifique
   */
  async getChartData(chartType: string, params: ChartDataParams = {}): Promise<ChartData> {
    const searchParams = new URLSearchParams();
    searchParams.append('period', String(params.period ?? 6));
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const response = await api.get(`/admin/analytics/charts/${chartType}?${searchParams.toString()}`);
    return response.data;
  }

  /**
   * Exporter un rapport en Excel
   */
  async exportToExcel(type?: string, startDate?: string, endDate?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/analytics/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Exporter un rapport en PDF
   */
  async exportToPDF(type?: string, startDate?: string, endDate?: string): Promise<Blob> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/admin/analytics/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Télécharger un fichier blob
   */
  downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const analyticsService = new AnalyticsService();
